/**
 * Minimal GraphQL-over-fetch client.
 *
 * The app talks to the NestJS API on a sibling subdomain
 * (`app.<ws>.channel-analyzer.dev` → `api.<ws>.channel-analyzer.dev`) and auth
 * is cookie-based, so every request MUST send credentials. The session and the
 * short-lived `ca_2fa_pending` cookie are httpOnly and travel automatically on
 * the shared `.channel-analyzer.dev` domain — no token is ever held in JS.
 */

export interface GraphQLErrorItem {
  message: string;
  extensions?: { code?: string; [key: string]: unknown };
}

/** Thrown for any transport or GraphQL-level failure. Carries the server error code. */
export class GraphQLRequestError extends Error {
  readonly errors: GraphQLErrorItem[];
  /** First error's `extensions.code` (e.g. `AUTH_INVALID_OTP`), when present. */
  readonly code?: string;

  constructor(message: string, errors: GraphQLErrorItem[] = []) {
    super(message);
    this.name = 'GraphQLRequestError';
    this.errors = errors;
    this.code = errors[0]?.extensions?.code as string | undefined;
  }
}

/**
 * Resolve the GraphQL endpoint. `VITE_API_URL` wins; otherwise derive it from
 * the current origin by swapping the `app.` host prefix for `api.` so the same
 * build works across every Conductor workspace without per-workspace config.
 */
function resolveEndpoint(): string {
  const explicit = import.meta.env.VITE_API_URL as string | undefined;
  if (explicit) return explicit;

  if (typeof window !== 'undefined' && window.location.origin.includes('://app.')) {
    return `${window.location.origin.replace('://app.', '://api.')}/graphql`;
  }
  return '/graphql';
}

const ENDPOINT = resolveEndpoint();

export async function gql<TData>(query: string, variables?: Record<string, unknown>): Promise<TData> {
  let response: Response;
  try {
    response = await fetch(ENDPOINT, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, variables: variables ?? {} }),
    });
  } catch {
    throw new GraphQLRequestError('Network error — could not reach the server.');
  }

  let payload: { data?: TData; errors?: GraphQLErrorItem[] };
  try {
    payload = (await response.json()) as { data?: TData; errors?: GraphQLErrorItem[] };
  } catch {
    throw new GraphQLRequestError(`Unexpected server response (HTTP ${response.status}).`);
  }

  if (payload.errors?.length) {
    const [first] = payload.errors;
    throw new GraphQLRequestError(first?.message ?? `Request failed (HTTP ${response.status}).`, payload.errors);
  }
  if (!response.ok || !payload.data) {
    throw new GraphQLRequestError(`Request failed (HTTP ${response.status}).`, payload.errors ?? []);
  }
  return payload.data;
}
